<template>
  <div>
    <ContentWrap title="主播选品">
      <el-alert
        class="anchor-goods-intro"
        type="info"
        :closable="false"
        title="选品仓（对齐抖音/淘宝直播习惯）：先给主播配可售商品池，开播时再从仓里挂到本场。仅「已关联且审核通过的商家 + 已导入直播库 + 上架」的商品会进仓并被端上看到；后台写入不会自动改本场挂车。"
      />
      <el-form :inline="true" class="mb8" @submit.prevent>
        <el-form-item label="主播">
          <LiveAnchorUserSelect v-model="queryParams.anchorUserId" />
        </el-form-item>
        <el-form-item label="商品SPU">
          <el-input
            v-model="queryParams.spuId"
            clearable
            placeholder="商品 SPU 编号"
            class="!w-180px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery"><Icon icon="ep:search" />查询</el-button>
          <el-button @click="resetQuery"><Icon icon="ep:refresh" />重置</el-button>
          <el-button
            type="primary"
            plain
            @click="handleAdd"
            v-hasPermi="['live:anchorGoods:create']"
          >
            <Icon icon="ep:plus" />新增
          </el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="list" border>
        <el-table-column label="编号" prop="id" width="80" align="center" />
        <el-table-column label="主播" min-width="200">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <el-avatar v-if="row.avatar" :src="row.avatar" :size="32" />
              <el-avatar v-else :size="32">{{ (row.nickname || '?').slice(0, 1) }}</el-avatar>
              <div>
                <div>{{ row.nickname || '未知主播' }}</div>
                <div class="text-12px text-[var(--el-text-color-secondary)]"
                  >用户编号 {{ row.anchorUserId }}</div
                >
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="商品" min-width="280">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <el-image
                v-if="row.picUrl"
                :src="row.picUrl"
                class="w-48px h-48px rounded shrink-0"
                fit="cover"
              />
              <div class="min-w-0">
                <div class="truncate" :title="row.spuName">{{
                  row.spuName || '商品不存在或已删除'
                }}</div>
                <div class="text-12px text-[var(--el-text-color-secondary)]">
                  SPU {{ row.spuId }}
                  <template v-if="row.price != null"> · ¥{{ formatYuan(row.price) }}</template>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="商品状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.spuStatus === 1" type="success">上架</el-tag>
            <el-tag v-else-if="row.spuStatus != null" type="info">下架</el-tag>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="端上可用" width="140" align="center">
          <template #default="{ row }">
            <el-tooltip
              v-if="row.usable === false"
              :content="row.usableReason || '端上不可见'"
              placement="top"
            >
              <el-tag type="danger">不可用</el-tag>
            </el-tooltip>
            <el-tag v-else-if="row.usable === true" type="success">可用</el-tag>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="排序" prop="sort" width="80" align="center" />
        <el-table-column
          label="创建时间"
          prop="createTime"
          min-width="170"
          :formatter="dateFormatter"
        />
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button
              link
              type="primary"
              @click="handleEdit(row)"
              v-hasPermi="['live:anchorGoods:update']"
              >编辑</el-button
            >
            <el-button
              link
              type="danger"
              @click="handleDelete(row)"
              v-hasPermi="['live:anchorGoods:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>

    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="560px" append-to-body>
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="主播" prop="anchorUserId">
          <LiveAnchorUserSelect
            v-model="form.anchorUserId"
            :disabled="!!form.id"
            select-class="!w-full"
            placeholder="搜索已通过认证的主播，或点选下方列表"
          />
          <div class="text-12px text-[var(--el-text-color-secondary)] mt-1"
            >仅展示审核通过的主播；需已关联审核通过的商家</div
          >
        </el-form-item>
        <el-form-item label="商品" prop="spuId">
          <div class="w-full">
            <div v-if="selectedSpu.spuId" class="flex items-center gap-2 mb-2">
              <el-image
                v-if="selectedSpu.picUrl"
                :src="selectedSpu.picUrl"
                class="w-48px h-48px rounded shrink-0"
                fit="cover"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate">{{ selectedSpu.spuName || `SPU ${selectedSpu.spuId}` }}</div>
                <div class="text-12px text-[var(--el-text-color-secondary)]">
                  SPU {{ selectedSpu.spuId }}
                  <template v-if="selectedSpu.shopId != null"> · 店铺 {{ selectedSpu.shopId }}</template>
                  <template v-if="selectedSpu.price != null">
                    · ¥{{ formatYuan(selectedSpu.price) }}
                  </template>
                </div>
              </div>
              <el-button
                v-if="!form.id"
                link
                type="danger"
                @click="clearSelectedSpu"
                >清除</el-button
              >
            </div>
            <el-button v-if="!form.id" @click="openSpuSelect">
              <Icon icon="ep:goods" />{{ selectedSpu.spuId ? '重新选择商品' : '选择商品' }}
            </el-button>
            <div class="text-12px text-[var(--el-text-color-secondary)] mt-1">
              {{
                form.id
                  ? '编辑时不可更换商品，如需调整请删除后重新新增'
                  : '须为该主播关联商家已导入直播库的上架商品；保存失败时请按接口提示处理商家绑定/导入'
              }}
            </div>
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number
            v-model="form.sort"
            :precision="0"
            :min="0"
            :max="9999"
            controls-position="right"
            class="!w-full"
          />
          <div class="text-12px text-[var(--el-text-color-secondary)] mt-1">数值越小越靠前</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>

    <SpuSelect ref="spuSelectRef" :radio="true" @confirm="onSpuConfirm" />
  </div>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  createAnchorGoods,
  updateAnchorGoods,
  deleteAnchorGoods,
  pageAnchorGoods,
  type LiveAnchorGoodsVO
} from '@/api/live/anchorGoods'
import * as ProductSpuApi from '@/api/mall/product/spu'
import SpuSelect from '@/views/mall/promotion/components/SpuSelect.vue'
import LiveAnchorUserSelect from '@/views/live/components/LiveAnchorUserSelect.vue'

defineOptions({ name: 'LiveAnchorGoods' })

const loading = ref(false)
const submitting = ref(false)
const list = ref<LiveAnchorGoodsVO[]>([])
const total = ref(0)
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref<any>(null)
const spuSelectRef = ref<InstanceType<typeof SpuSelect>>()

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  anchorUserId: undefined as number | undefined,
  spuId: undefined as string | undefined
})

const form = reactive({
  id: undefined as number | undefined,
  anchorUserId: undefined as number | undefined,
  spuId: undefined as number | undefined,
  sort: 0
})

const selectedSpu = reactive({
  spuId: undefined as number | undefined,
  spuName: '' as string,
  picUrl: '' as string,
  price: undefined as number | undefined,
  shopId: undefined as number | undefined
})

const rules = {
  anchorUserId: [{ required: true, message: '请选择主播', trigger: 'change' }],
  spuId: [{ required: true, message: '请选择商品', trigger: 'change' }],
  sort: [{ required: true, message: '排序不能为空', trigger: 'change' }]
}

const formatYuan = (fen?: number) => ((Number(fen) || 0) / 100).toFixed(2)

const toOptionalNumber = (value?: string) => {
  if (value == null || String(value).trim() === '') return undefined
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

const getList = async () => {
  loading.value = true
  try {
    const data: any = await pageAnchorGoods({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      anchorUserId: queryParams.anchorUserId,
      spuId: toOptionalNumber(queryParams.spuId)
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.anchorUserId = undefined
  queryParams.spuId = undefined
  handleQuery()
}

const clearSelectedSpu = () => {
  form.spuId = undefined
  selectedSpu.spuId = undefined
  selectedSpu.spuName = ''
  selectedSpu.picUrl = ''
  selectedSpu.price = undefined
  selectedSpu.shopId = undefined
}

const resetForm = () => {
  form.id = undefined
  form.anchorUserId = undefined
  form.spuId = undefined
  form.sort = 0
  clearSelectedSpu()
}

const applySelectedSpu = (spu: {
  id?: number
  name?: string
  picUrl?: string
  price?: number
  shopId?: number
}) => {
  form.spuId = spu.id
  selectedSpu.spuId = spu.id
  selectedSpu.spuName = spu.name || ''
  selectedSpu.picUrl = spu.picUrl || ''
  selectedSpu.price = spu.price
  selectedSpu.shopId = spu.shopId
}

const openSpuSelect = () => {
  spuSelectRef.value?.open()
}

const onSpuConfirm = async (spuId: number) => {
  try {
    const detail: any = await ProductSpuApi.getSpu(spuId)
    applySelectedSpu(detail || { id: spuId })
    formRef.value?.validateField?.('spuId')
  } catch {
    applySelectedSpu({ id: spuId })
  }
}

const handleAdd = () => {
  resetForm()
  dialogTitle.value = '新增主播选品'
  dialogVisible.value = true
}

const handleEdit = (row: LiveAnchorGoodsVO) => {
  resetForm()
  form.id = row.id
  form.anchorUserId = row.anchorUserId
  form.spuId = row.spuId
  form.sort = row.sort ?? 0
  applySelectedSpu({
    id: row.spuId,
    name: row.spuName,
    picUrl: row.picUrl,
    price: row.price,
    shopId: row.shopId
  })
  dialogTitle.value = '编辑主播选品'
  dialogVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  if (!form.spuId) {
    ElMessage.warning('请选择商品')
    return
  }
  submitting.value = true
  try {
    const payload = {
      id: form.id,
      anchorUserId: form.anchorUserId,
      spuId: form.spuId,
      sort: form.sort ?? 0
    }
    if (form.id) {
      await updateAnchorGoods(payload)
      ElMessage.success('修改成功')
    } else {
      await createAnchorGoods(payload)
      ElMessage.success('新增成功')
    }
    dialogVisible.value = false
    getList()
  } finally {
    submitting.value = false
  }
}

const handleDelete = (row: LiveAnchorGoodsVO) => {
  ElMessageBox.confirm(
    `确认删除主播「${row.nickname || row.anchorUserId}」的选品「${row.spuName || row.spuId}」吗？`,
    '提示',
    { type: 'warning' }
  ).then(async () => {
    await deleteAnchorGoods(row.id!)
    ElMessage.success('删除成功')
    getList()
  })
}

onMounted(() => {
  getList()
})
</script>

<style scoped>
.anchor-goods-intro {
  margin-bottom: 16px;
}
</style>
